import { useState, useMemo, useEffect } from 'react';
import { GitCompare, SplitSquareHorizontal, List, Edit3, Trash2 } from 'lucide-react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { diffLines, Change } from 'diff';
import hljs from 'highlight.js';

interface DiffViewerProps {
  onAutoCollapse?: () => void;
}

export default function DiffViewer({ onAutoCollapse }: DiffViewerProps = {}) {
  const [leftInput, setLeftInput] = useState('');
  const [rightInput, setRightInput] = useState('');
  const [viewMode, setViewMode] = useState<'split' | 'unified'>('split');
  const [showInputs, setShowInputs] = useState(true);
  const [hasAutoCollapsed, setHasAutoCollapsed] = useState(false);

  const getStats = (text: string) => {
    return {
      lines: text.split('\n').length,
      chars: text.length,
      bytes: new Blob([text]).size,
    };
  };

  const leftStats = getStats(leftInput);
  const rightStats = getStats(rightInput);
  const sizeDiff = rightStats.bytes - leftStats.bytes;
  const sizePercent = leftStats.bytes > 0
    ? ((sizeDiff / leftStats.bytes) * 100).toFixed(1)
    : '0.0';

  // Compute diff
  const diff = useMemo(() => {
    if (!leftInput || !rightInput) return [];
    return diffLines(leftInput, rightInput);
  }, [leftInput, rightInput]);

  // Calculate detailed diff metrics
  const diffMetrics = useMemo(() => {
    let added = 0;
    let removed = 0;
    let unchanged = 0;

    diff.forEach((part: Change) => {
      const lines = part.value.split('\n').filter((line, i, arr) => i < arr.length - 1 || line);
      if (part.added) {
        added += lines.length;
      } else if (part.removed) {
        removed += lines.length;
      } else {
        unchanged += lines.length;
      }
    });

    return { added, removed, unchanged, total: added + removed + unchanged };
  }, [diff]);

  // Auto-detect language from input
  const detectedLanguage = useMemo(() => {
    const sampleText = leftInput || rightInput;
    if (!sampleText || sampleText.length < 10) return 'text';

    try {
      // Check for JSX/TSX patterns
      const hasJSXElements = /<[A-Z][a-zA-Z0-9]*[\s/>]/.test(sampleText) || // <Component
                              /<[a-z]+[\s>]/.test(sampleText); // <div
      const hasTypeScript = /:\s*[A-Z][a-zA-Z0-9<>[\],\s]*[=;]/.test(sampleText) || // type annotations
                             /interface\s+[A-Z]/.test(sampleText) ||
                             /type\s+[A-Z]/.test(sampleText) ||
                             /<.*>/.test(sampleText) && /=>/.test(sampleText); // generic types with arrow functions

      // If it has JSX elements, detect JSX or TSX
      if (hasJSXElements) {
        if (hasTypeScript) {
          return 'tsx';
        }
        return 'jsx';
      }

      const result = hljs.highlightAuto(sampleText.slice(0, 500)); // Sample first 500 chars

      // Map highlight.js language names to Prism language names
      const languageMap: { [key: string]: string } = {
        'javascript': 'javascript',
        'typescript': 'typescript',
        'python': 'python',
        'java': 'java',
        'cpp': 'cpp',
        'c': 'c',
        'csharp': 'csharp',
        'php': 'php',
        'ruby': 'ruby',
        'go': 'go',
        'rust': 'rust',
        'swift': 'swift',
        'kotlin': 'kotlin',
        'scala': 'scala',
        'json': 'json',
        'xml': 'markup',
        'html': 'markup',
        'css': 'css',
        'scss': 'scss',
        'less': 'less',
        'sql': 'sql',
        'bash': 'bash',
        'shell': 'bash',
        'yaml': 'yaml',
        'markdown': 'markdown',
        'plaintext': 'text',
      };

      const detected = result.language || 'text';
      return languageMap[detected] || detected;
    } catch (error) {
      return 'text';
    }
  }, [leftInput, rightInput]);

  // Auto-collapse sidebar and hide inputs when both files have content
  useEffect(() => {
    if (leftInput && rightInput && !hasAutoCollapsed) {
      if (onAutoCollapse) {
        onAutoCollapse();
      }
      setShowInputs(false);
      setHasAutoCollapsed(true);
    }
  }, [leftInput, rightInput, hasAutoCollapsed, onAutoCollapse]);

  const renderUnifiedDiff = () => {
    if (!leftInput || !rightInput) {
      return (
        <div className="text-center py-16 text-gray-500 dark:text-dark-muted">
          <GitCompare className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p className="text-lg font-semibold mb-2">No Comparison Yet</p>
          <p className="text-sm">Paste content in both textareas above to see the diff</p>
        </div>
      );
    }

    let lineNum = 1;
    return (
      <div className="text-xs min-w-max" style={{ fontFamily: '"JetBrains Mono", "Fira Code", "Cascadia Code", "SF Mono", Monaco, "Courier New", monospace' }}>
        {diff.map((part: Change, idx: number) => {
          const lines = part.value.split('\n').filter((line, i, arr) => i < arr.length - 1 || line);
          const bgColor = part.added
            ? 'bg-green-900/40 border-l-4 border-green-500 shadow-sm'
            : part.removed
            ? 'bg-red-900/40 border-l-4 border-red-500 shadow-sm'
            : 'bg-transparent hover:bg-gray-800/30';

          return lines.map((line, lineIdx) => {
            const currentLineNum = !part.removed ? lineNum++ : '';
            return (
              <div
                key={`${idx}-${lineIdx}`}
                className={`${bgColor} flex transition-colors hover:bg-opacity-80`}
              >
                <span className="inline-block w-14 text-center text-gray-400 dark:text-gray-500 select-none flex-shrink-0 py-1 font-semibold">
                  {currentLineNum}
                </span>
                <span className="inline-block w-8 text-center text-gray-400 dark:text-gray-500 select-none flex-shrink-0 py-1 font-bold">
                  {part.added ? '+' : part.removed ? '-' : ' '}
                </span>
                <div className="flex-1 min-w-0">
                  <SyntaxHighlighter
                    language={detectedLanguage}
                    style={vscDarkPlus}
                    customStyle={{
                      margin: 0,
                      padding: '4px 12px',
                      background: 'transparent',
                      fontSize: '13px',
                      lineHeight: '1.6',
                      fontFamily: '"JetBrains Mono", "Fira Code", "Cascadia Code", "SF Mono", Monaco, "Courier New", monospace',
                      fontWeight: 500,
                      letterSpacing: '0.2px',
                    }}
                    codeTagProps={{
                      style: {
                        background: 'transparent',
                        fontFamily: 'inherit',
                      },
                    }}
                    PreTag="span"
                  >
                    {line || ' '}
                  </SyntaxHighlighter>
                </div>
              </div>
            );
          });
        })}
      </div>
    );
  };

  const renderSplitDiff = () => {
    if (!leftInput && !rightInput) {
      return (
        <div className="text-center py-16 text-gray-500 dark:text-dark-muted">
          <GitCompare className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p className="text-lg font-semibold mb-2">No Comparison Yet</p>
          <p className="text-sm">Paste content in both textareas above to see the side-by-side comparison</p>
        </div>
      );
    }

    // Build synchronized line arrays for split view with diff highlighting
    const leftLines: { content: string; lineNum: number; type: 'removed' | 'unchanged' | 'empty' }[] = [];
    const rightLines: { content: string; lineNum: number; type: 'added' | 'unchanged' | 'empty' }[] = [];

    let leftLineNum = 1;
    let rightLineNum = 1;

    diff.forEach((part: Change) => {
      const lines = part.value.split('\n').filter((line, i, arr) => i < arr.length - 1 || line);

      if (part.removed) {
        // Lines only in left (removed)
        lines.forEach(line => {
          leftLines.push({ content: line, lineNum: leftLineNum++, type: 'removed' });
          rightLines.push({ content: '', lineNum: 0, type: 'empty' });
        });
      } else if (part.added) {
        // Lines only in right (added)
        lines.forEach(line => {
          leftLines.push({ content: '', lineNum: 0, type: 'empty' });
          rightLines.push({ content: line, lineNum: rightLineNum++, type: 'added' });
        });
      } else {
        // Unchanged lines in both
        lines.forEach(line => {
          leftLines.push({ content: line, lineNum: leftLineNum++, type: 'unchanged' });
          rightLines.push({ content: line, lineNum: rightLineNum++, type: 'unchanged' });
        });
      }
    });

    return (
      <div className="grid grid-cols-2 gap-3">
        {/* Left - File A */}
        <div>
          <div className="flex items-center justify-between mb-1.5 px-2">
            <label className="text-xs font-semibold text-gray-600 dark:text-gray-400">
              Original (File A)
            </label>
            <span className="text-xs text-gray-500 dark:text-dark-muted">
              {leftStats.lines}L • {leftStats.bytes}B
            </span>
          </div>
          <div className="border border-gray-300 dark:border-gray-700 rounded-xl overflow-x-auto bg-[#1e1e1e] shadow-lg scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
            <div className="text-xs min-w-max" style={{ fontFamily: '"JetBrains Mono", "Fira Code", "Cascadia Code", "SF Mono", Monaco, "Courier New", monospace' }}>
              {leftLines.map((line, idx) => (
                <div
                  key={idx}
                  className={`flex transition-colors hover:bg-opacity-80 ${
                    line.type === 'removed'
                      ? 'bg-red-900/40 border-l-4 border-red-500 shadow-sm'
                      : line.type === 'empty'
                      ? 'bg-gray-800/60'
                      : 'bg-transparent hover:bg-gray-800/30'
                  }`}
                >
                  <span className="inline-block w-14 text-center text-gray-400 dark:text-gray-500 select-none flex-shrink-0 py-1 font-semibold text-xs">
                    {line.lineNum || ''}
                  </span>
                  <div className="flex-1 min-w-0">
                    <SyntaxHighlighter
                      language={detectedLanguage}
                      style={vscDarkPlus}
                      customStyle={{
                        margin: 0,
                        padding: '4px 12px',
                        background: 'transparent',
                        fontSize: '13px',
                        lineHeight: '1.6',
                        fontFamily: '"JetBrains Mono", "Fira Code", "Cascadia Code", "SF Mono", Monaco, "Courier New", monospace',
                        fontWeight: 500,
                        letterSpacing: '0.2px',
                      }}
                      codeTagProps={{
                        style: {
                          background: 'transparent',
                          fontFamily: 'inherit',
                        },
                      }}
                      PreTag="span"
                    >
                      {line.content || ' '}
                    </SyntaxHighlighter>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right - File B */}
        <div>
          <div className="flex items-center justify-between mb-1.5 px-2">
            <label className="text-xs font-semibold text-gray-600 dark:text-gray-400">
              Modified (File B)
            </label>
            <span className="text-xs text-gray-500 dark:text-dark-muted">
              {rightStats.lines}L • {rightStats.bytes}B
            </span>
          </div>
          <div className="border border-gray-300 dark:border-gray-700 rounded-xl overflow-x-auto bg-[#1e1e1e] shadow-lg scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
            <div className="text-xs min-w-max" style={{ fontFamily: '"JetBrains Mono", "Fira Code", "Cascadia Code", "SF Mono", Monaco, "Courier New", monospace' }}>
              {rightLines.map((line, idx) => (
                <div
                  key={idx}
                  className={`flex transition-colors hover:bg-opacity-80 ${
                    line.type === 'added'
                      ? 'bg-green-900/40 border-l-4 border-green-500 shadow-sm'
                      : line.type === 'empty'
                      ? 'bg-gray-800/60'
                      : 'bg-transparent hover:bg-gray-800/30'
                  }`}
                >
                  <span className="inline-block w-14 text-center text-gray-400 dark:text-gray-500 select-none flex-shrink-0 py-1 font-semibold text-xs">
                    {line.lineNum || ''}
                  </span>
                  <div className="flex-1 min-w-0">
                    <SyntaxHighlighter
                      language={detectedLanguage}
                      style={vscDarkPlus}
                      customStyle={{
                        margin: 0,
                        padding: '4px 12px',
                        background: 'transparent',
                        fontSize: '13px',
                        lineHeight: '1.6',
                        fontFamily: '"JetBrains Mono", "Fira Code", "Cascadia Code", "SF Mono", Monaco, "Courier New", monospace',
                        fontWeight: 500,
                        letterSpacing: '0.2px',
                      }}
                      codeTagProps={{
                        style: {
                          background: 'transparent',
                          fontFamily: 'inherit',
                        },
                      }}
                      PreTag="span"
                    >
                      {line.content || ' '}
                    </SyntaxHighlighter>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-white dark:from-dark-bg dark:to-dark-surface rounded-2xl p-6 border-2 border-gray-200 dark:border-dark-border flex flex-col shadow-lg">
      {/* Controls Bar */}
      <div className="flex items-center justify-end mb-4 flex-shrink-0 p-3 bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border shadow-sm">
        {/* View Mode Switcher & Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowInputs(!showInputs)}
            className="px-3 py-2 text-sm rounded-lg font-medium transition-all flex items-center gap-2 bg-gray-100 dark:bg-dark-bg text-gray-700 dark:text-dark-text hover:bg-gray-200 dark:hover:bg-dark-border shadow-sm hover:shadow"
            title={showInputs ? 'Hide inputs' : 'Show inputs'}
          >
            <Edit3 className="w-4 h-4" />
            <span className="hidden sm:inline">{showInputs ? 'Hide' : 'Edit'}</span>
          </button>
          <button
            onClick={() => {
              setLeftInput('');
              setRightInput('');
              setShowInputs(true);
              setHasAutoCollapsed(false);
            }}
            className="px-3 py-2 text-sm rounded-lg font-medium transition-all flex items-center gap-2 bg-gray-100 dark:bg-dark-bg text-gray-700 dark:text-dark-text hover:bg-gray-200 dark:hover:bg-dark-border shadow-sm hover:shadow"
            title="Clear all"
          >
            <Trash2 className="w-4 h-4" />
            <span className="hidden sm:inline">Clear</span>
          </button>
          <div className="w-px h-8 bg-gray-300 dark:bg-dark-border mx-1"></div>
          <button
            onClick={() => setViewMode('split')}
            className={`px-4 py-2 text-sm rounded-lg font-semibold transition-all flex items-center gap-2 shadow-sm hover:shadow ${
              viewMode === 'split'
                ? 'bg-gradient-to-r from-coral-500 to-teal-500 text-white shadow-lg'
                : 'bg-gray-100 dark:bg-dark-bg text-gray-700 dark:text-dark-text hover:bg-gray-200 dark:hover:bg-dark-border'
            }`}
          >
            <SplitSquareHorizontal className="w-4 h-4" />
            <span>Split</span>
          </button>
          <button
            onClick={() => setViewMode('unified')}
            className={`px-4 py-2 text-sm rounded-lg font-semibold transition-all flex items-center gap-2 shadow-sm hover:shadow ${
              viewMode === 'unified'
                ? 'bg-gradient-to-r from-coral-500 to-teal-500 text-white shadow-lg'
                : 'bg-gray-100 dark:bg-dark-bg text-gray-700 dark:text-dark-text hover:bg-gray-200 dark:hover:bg-dark-border'
            }`}
          >
            <List className="w-4 h-4" />
            <span>Unified</span>
          </button>
        </div>
      </div>

      {/* Comprehensive Diff Metrics Banner */}
      {leftInput && rightInput && (
        <div className="mb-4 p-4 bg-gradient-to-r from-purple-50 via-blue-50 to-teal-50 dark:from-purple-900/20 dark:via-blue-900/20 dark:to-teal-900/20 rounded-xl border-2 border-purple-200 dark:border-purple-800 shadow-md flex-shrink-0">
          <div className="mb-3">
            <h3 className="text-sm font-bold text-gray-900 dark:text-dark-text uppercase tracking-wide flex items-center gap-2">
              <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
              Comparison Summary
            </h3>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {/* Lines Added */}
            <div className="bg-white dark:bg-dark-surface rounded-lg p-3 border-2 border-green-200 dark:border-green-800 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-xs font-semibold text-green-700 dark:text-green-400 uppercase">Added</span>
              </div>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">+{diffMetrics.added}</p>
              <p className="text-xs text-gray-500 dark:text-dark-muted">lines</p>
            </div>

            {/* Lines Removed */}
            <div className="bg-white dark:bg-dark-surface rounded-lg p-3 border-2 border-red-200 dark:border-red-800 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="text-xs font-semibold text-red-700 dark:text-red-400 uppercase">Removed</span>
              </div>
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">-{diffMetrics.removed}</p>
              <p className="text-xs text-gray-500 dark:text-dark-muted">lines</p>
            </div>

            {/* Lines Unchanged */}
            <div className="bg-white dark:bg-dark-surface rounded-lg p-3 border-2 border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-400 uppercase">Unchanged</span>
              </div>
              <p className="text-2xl font-bold text-gray-600 dark:text-gray-400">{diffMetrics.unchanged}</p>
              <p className="text-xs text-gray-500 dark:text-dark-muted">lines</p>
            </div>

            {/* Size Change */}
            <div className="bg-white dark:bg-dark-surface rounded-lg p-3 border-2 border-blue-200 dark:border-blue-800 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-xs font-semibold text-blue-700 dark:text-blue-400 uppercase">Size Δ</span>
              </div>
              <p className={`text-2xl font-bold ${
                sizeDiff < 0 ? 'text-green-600 dark:text-green-400' : sizeDiff > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'
              }`}>
                {sizeDiff > 0 ? '+' : ''}{sizeDiff}
              </p>
              <p className="text-xs text-gray-500 dark:text-dark-muted">bytes ({parseFloat(sizePercent) > 0 ? '+' : ''}{sizePercent}%)</p>
            </div>

            {/* Total Changes */}
            <div className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg p-3 border-2 border-purple-300 dark:border-purple-700 shadow-md">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-3 h-3 bg-white rounded-full"></div>
                <span className="text-xs font-semibold text-white uppercase">Changes</span>
              </div>
              <p className="text-2xl font-bold text-white">{diffMetrics.added + diffMetrics.removed}</p>
              <p className="text-xs text-white/80">total modifications</p>
            </div>
          </div>
        </div>
      )}

      {/* Input Textareas - Always accessible */}
      {showInputs && (
        <div className="mb-3 grid grid-cols-2 gap-3 flex-shrink-0">
          <div>
            <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 block flex items-center gap-1.5">
              <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
              File A (Original)
            </label>
            <textarea
              value={leftInput}
              onChange={(e) => setLeftInput(e.target.value)}
              placeholder="Paste your original code here..."
              className="w-full h-48 p-3 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-xs bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-coral-400 focus:border-transparent resize-none transition-all"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 block flex items-center gap-1.5">
              <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
              File B (Modified)
            </label>
            <textarea
              value={rightInput}
              onChange={(e) => setRightInput(e.target.value)}
              placeholder="Paste your modified code here..."
              className="w-full h-48 p-3 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-xs bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-coral-400 focus:border-transparent resize-none transition-all"
            />
          </div>
        </div>
      )}

      {/* Content Area - Expands with content, page scrolls naturally */}
      <div className="flex-shrink-0">
        {viewMode === 'unified' ? (
          <div className="border border-gray-300 dark:border-gray-700 rounded-xl overflow-x-auto bg-[#1e1e1e] shadow-xl scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
            {renderUnifiedDiff()}
          </div>
        ) : (
          renderSplitDiff()
        )}
      </div>
    </div>
  );
}
